/**
 * The Participants editor's rows for an AUTOMATIC merge (standalone desktop
 * calls): collapsed under the row its lines went into, with "Split back", or
 * split back out as an editable row with Undo. Rules: participantSplit.ts.
 * Same layout and copy as the end-of-call review in both desktop apps.
 */
import React from 'react';
import { ParticipantDraft, truncateQuote } from './participantRows';
import { AutoMerge, defaultSplitName } from './participantSplit';
import { ColorDot, RoleToggle, SAME_AS_SLOT_CLASS } from './ParticipantRowParts';
import {
  formatAutoMergedRow,
  formatFirstLine,
  formatLines,
  formatSplitBackLabel,
  formatTurns,
  PARTICIPANT_MERGE_COPY,
} from '../../constants/participantMerge';

const LINK_BUTTON_CLASS =
  'flex-shrink-0 py-1 text-sm font-medium text-signature hover:text-signature-strong hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signature disabled:opacity-50';

/** Indents a sub-row under the target row it belongs to. */
const SUB_ROW_INDENT_CLASS = 'pl-6';

/** How a merge is named where it has no text: its line count. */
const describeMerge = (merge: AutoMerge): string =>
  merge.firstLine ? truncateQuote(merge.firstLine) : formatLines(merge.lineCount);

/** The split row's stable label: its default name ("Customer N"), else how the merge is described. */
export const splitRowLabel = (merge: AutoMerge): string => defaultSplitName(merge.sourceId) || describeMerge(merge);

interface AutoMergedRowProps {
  merge: AutoMerge;
  targetName: string;
  disabled: boolean;
  /** Absent on a read-only sheet: the row shows no Split back. */
  onSplitBack?: () => void;
}

/** “<first line>” (N lines) · merged automatically into <target>   [Split back] */
export const AutoMergedRow: React.FC<AutoMergedRowProps> = ({ merge, targetName, disabled, onSplitBack }) => (
  <li className={`flex items-center gap-3 py-1 ${SUB_ROW_INDENT_CLASS}`} data-testid="auto-merged-row">
    <span className="flex-grow min-w-0 truncate text-xs text-ink-muted" title={merge.firstLine || undefined}>
      {formatAutoMergedRow(merge.firstLine ? truncateQuote(merge.firstLine) : '', merge.lineCount, targetName)}
    </span>
    {onSplitBack && (
      <button type="button" onClick={onSplitBack} disabled={disabled}
              aria-label={formatSplitBackLabel(describeMerge(merge), targetName)} className={LINK_BUTTON_CLASS}>
        {PARTICIPANT_MERGE_COPY.splitBack}
      </button>
    )}
  </li>
);

interface SplitBackRowProps {
  merge: AutoMerge;
  draft: ParticipantDraft;
  /** Stable accessible name for the row's controls while the name is being typed. */
  label: string;
  color: string;
  saving: boolean;
  onChange: (change: Partial<ParticipantDraft>) => void;
  onUndo: () => void;
}

/** dot · [name / first line] · Salesperson|Customer · N turns · Undo (in the "Same as…" slot: no chains). */
export const SplitBackRow: React.FC<SplitBackRowProps> = ({ merge, draft, label, color, saving, onChange, onUndo }) => (
  <li className={`flex items-start gap-3 py-1.5 ${SUB_ROW_INDENT_CLASS}`} data-testid="split-back-row">
    <ColorDot color={color} />
    <div className="flex-grow min-w-0">
      <input
        aria-label={`Name of ${label}`}
        placeholder={PARTICIPANT_MERGE_COPY.namePlaceholder}
        className="w-full text-sm border border-hairline rounded px-2 py-1"
        value={draft.name}
        maxLength={100}
        disabled={saving}
        onChange={(event) => onChange({ name: event.target.value })}
      />
      {merge.firstLine && (
        <p className="mt-0.5 px-0.5 truncate text-xs text-ink-muted" title={merge.firstLine}>
          {formatFirstLine(truncateQuote(merge.firstLine))}
        </p>
      )}
    </div>
    <RoleToggle value={draft.role} disabled={saving} personName={label} onChange={(role) => onChange({ role })} />
    <span className="w-16 flex-shrink-0 py-1 text-right text-xs text-ink-muted">{formatTurns(merge.lineCount)}</span>
    <div className={SAME_AS_SLOT_CLASS}>
      <button type="button" onClick={onUndo} disabled={saving} className={LINK_BUTTON_CLASS}>
        {PARTICIPANT_MERGE_COPY.undo}
      </button>
    </div>
  </li>
);
